import { useCallback, useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query, where, type Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  ArrowClockwise,
  BookOpen,
  ChartLineUp,
  CheckCircle,
  CurrencyCircleDollar,
  GearSix,
  GlobeHemisphereWest,
  ShieldCheck,
  Warning,
} from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Input, Spinner } from "@/components/ui";
import { db, functions } from "@/config/firebase";
import { ALL_STATES, type Project, type StateMachineState } from "@/types";
import { LogoMark, PressEyebrow, PressLogo, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";

interface PipelineStats {
  byState: Record<string, number>;
  total: number;
  active: number;
  completed: number;
  errors: number;
}

interface RoyaltyDashboard {
  total_gross_revenue: number;
  total_net_royalty: number;
  total_operator_share: number;
  total_author_payouts: number;
  total_units_sold: number;
  catalog_size: number;
}

interface ISBNInventoryStatus {
  total: number;
  available: number;
  assigned: number;
  published: number;
}

interface ISBNImportResult extends ISBNInventoryStatus {
  imported: number;
}

interface SystemStatus {
  status: "healthy" | "degraded" | "unknown";
  active_projects: number;
  published_books: number;
  isbn_inventory: number;
  stalled_projects: number;
  errors_last_hour: number;
  last_check: string | null;
  circuit_breakers: {
    breaker_id: string;
    name: string;
    tripped: boolean;
    trip_count: number;
  }[];
}

interface RoyaltyReportEntry {
  isbn: string;
  channel: "amazon_kdp" | "ingramspark" | "draft2digital" | "storyforge_direct";
  period: string;
  units_sold: number;
  gross_revenue: number;
  platform_fees: number;
  print_cost: number;
}

interface RoyaltyImportResult {
  entries_imported: number;
  entries_skipped: number;
  total_net_royalty: number;
}

interface PayoutResult {
  authors_paid: number;
  total_paid: number;
  failures: string[];
  stripe_transfers: string[];
}

interface CostAlert {
  project_id: string;
  alert_type: string;
  actual_cost_usd: number;
}

interface ContactQueueItem {
  id: string;
  intent: "consultation" | "sales" | "support";
  name: string;
  email: string;
  organization: string;
  message: string;
  status: string;
  created_at?: Timestamp;
}

const ROYALTY_TEMPLATE = "[]";

const NAV = [
  { label: "Overview", href: "#overview" },
  { label: "Books", href: "#projects" },
  { label: "Authors", href: "#authors" },
  { label: "Production", href: "#production" },
  { label: "Quality", href: "#quality" },
  { label: "Payouts", href: "#payouts" },
  { label: "Analytics", href: "#analytics" },
  { label: "Channels", href: "#channels" },
  { label: "Orders", href: "#orders" },
  { label: "Support", href: "#support" },
  { label: "System", href: "#system" },
] as const;

export function OperatorDashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [royalty, setRoyalty] = useState<RoyaltyDashboard | null>(null);
  const [isbnStatus, setIsbnStatus] = useState<ISBNInventoryStatus | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [costAlerts, setCostAlerts] = useState<CostAlert[]>([]);
  const [contacts, setContacts] = useState<ContactQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [opsMessage, setOpsMessage] = useState<string | null>(null);
  const [isbnNumbers, setIsbnNumbers] = useState("");
  const [isbnBatch, setIsbnBatch] = useState("");
  const [royaltyJson, setRoyaltyJson] = useState(ROYALTY_TEMPLATE);

  useEffect(() => {
    const projectsQuery = query(collection(db, "projects"), orderBy("updated_at", "desc"));
    return onSnapshot(projectsQuery, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Project);
      setProjects(all);
      setStats(buildPipelineStats(all));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const alertsQuery = query(collection(db, "cost_alerts"), where("acknowledged", "==", false));
    return onSnapshot(alertsQuery, (snap) => {
      setCostAlerts(snap.docs.map((d) => d.data() as CostAlert));
    });
  }, []);

  useEffect(() => {
    const contactsQuery = query(collection(db, "contact_requests"), orderBy("created_at", "desc"));
    return onSnapshot(contactsQuery, (snap) => {
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ContactQueueItem));
    });
  }, []);

  const loadOperationalData = useCallback(async () => {
    setOpsLoading(true);
    setOpsError(null);
    try {
      const getRoyaltyDashboard = httpsCallable<Record<string, never>, RoyaltyDashboard>(functions, "getRoyaltyDashboard");
      const getISBNInventoryStatus = httpsCallable<Record<string, never>, ISBNInventoryStatus>(functions, "getISBNInventoryStatus");
      const getSystemStatus = httpsCallable<Record<string, never>, SystemStatus>(functions, "getSystemStatus");
      const [royaltyResult, isbnResult, systemResult] = await Promise.all([
        getRoyaltyDashboard({}),
        getISBNInventoryStatus({}),
        getSystemStatus({}),
      ]);
      setRoyalty(royaltyResult.data);
      setIsbnStatus(isbnResult.data);
      setSystemStatus(systemResult.data);
    } catch (err) {
      setOpsError(err instanceof Error ? err.message : "Could not load operator metrics.");
    } finally {
      setOpsLoading(false);
    }
  }, []);

  useEffect(() => { void loadOperationalData(); }, [loadOperationalData]);

  const importISBNs = async (event: FormEvent) => {
    event.preventDefault();
    setOpsLoading(true);
    setOpsError(null);
    setOpsMessage(null);
    try {
      const parsed = isbnNumbers.split(/[\s,]+/).map((isbn) => isbn.trim()).filter(Boolean);
      const importISBNInventory = httpsCallable<{ isbn_numbers: string[]; purchase_batch: string }, ISBNImportResult>(functions, "importISBNInventory");
      const result = await importISBNInventory({ isbn_numbers: parsed, purchase_batch: isbnBatch.trim() });
      setIsbnStatus(result.data);
      setIsbnNumbers("");
      setOpsMessage(`Imported ${result.data.imported} ISBNs. ${result.data.available} available.`);
    } catch (err) {
      setOpsError(err instanceof Error ? err.message : "ISBN import failed.");
    } finally {
      setOpsLoading(false);
    }
  };

  const importRoyaltyReport = async (event: FormEvent) => {
    event.preventDefault();
    setOpsLoading(true);
    setOpsError(null);
    setOpsMessage(null);
    try {
      const entries = parseRoyaltyEntries(royaltyJson);
      const importRoyaltyReportFn = httpsCallable<{ entries: RoyaltyReportEntry[] }, RoyaltyImportResult>(functions, "importRoyaltyReport");
      const result = await importRoyaltyReportFn({ entries });
      setOpsMessage(`Imported ${result.data.entries_imported} royalty entries, skipped ${result.data.entries_skipped}, net $${result.data.total_net_royalty.toFixed(2)}.`);
      await loadOperationalData();
    } catch (err) {
      setOpsError(err instanceof Error ? err.message : "Royalty import failed.");
    } finally {
      setOpsLoading(false);
    }
  };

  const processPayouts = async () => {
    setOpsLoading(true);
    setOpsError(null);
    setOpsMessage(null);
    try {
      const processPayoutsFn = httpsCallable<Record<string, never>, PayoutResult>(functions, "processPayouts");
      const result = await processPayoutsFn({});
      const failureSuffix = result.data.failures.length > 0 ? ` Failures: ${result.data.failures.join("; ")}.` : "";
      setOpsMessage(`Paid ${result.data.authors_paid} authors, total $${result.data.total_paid.toFixed(2)}.${failureSuffix}`);
      await loadOperationalData();
    } catch (err) {
      setOpsError(err instanceof Error ? err.message : "Payout processing failed.");
    } finally {
      setOpsLoading(false);
    }
  };

  if (loading) {
    return <AppShell variant="operator"><div className="flex justify-center py-20"><Spinner /></div></AppShell>;
  }

  return (
    <AppShell variant="operator">
      <div className="min-h-[100dvh] bg-paper">
        <aside className="fixed bottom-0 left-0 top-0 z-40 hidden w-20 bg-ink-1 text-paper-raised lg:flex lg:flex-col">
          <div className="grid place-items-center border-b border-[rgba(255,253,248,0.14)] p-4">
            <LogoMark className="h-11 w-10 text-walnut-soft" />
          </div>
          <nav className="flex-1 space-y-1 p-2">
            {NAV.map((item, index) => (
              <a key={item.href} href={item.href} className={`block rounded-as-2 px-1.5 py-2.5 text-center font-mono text-[8px] uppercase tracking-[0.08em] ${index === 0 ? "bg-[rgba(255,253,248,0.12)] text-paper-raised" : "text-[rgba(255,253,248,0.72)] hover:text-paper-raised"}`}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="border-t border-[rgba(255,253,248,0.14)] p-3 text-center font-mono text-[9px] uppercase tracking-[0.08em] text-[rgba(255,253,248,0.7)]">
            Operator
          </div>
        </aside>

        <div className="lg:pl-20">
          <main className="mx-auto max-w-[1360px] px-4 py-5 sm:px-6 lg:px-8">
            <header className="mb-5 flex flex-col justify-between gap-4 border-b border-rule pb-5 lg:flex-row lg:items-end">
              <div>
                <PressEyebrow>PullPress · Book production room</PressEyebrow>
                <div className="mt-3"><PressLogo size="md" /></div>
                <p className="mt-2 font-text text-base text-ink-2">Operator Dashboard</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="rounded-as-2 border border-rule bg-paper-raised px-4 py-2 font-ui text-sm text-ink-2">{formatDateRange(new Date())}</div>
                <div className="rounded-as-2 border border-rule bg-paper-raised px-4 py-2 font-ui text-sm text-ink-2">Live operator data</div>
                <Button variant="walnut" onClick={loadOperationalData} disabled={opsLoading}>
                  <ArrowClockwise size={16} className="mr-2" />
                  {opsLoading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </header>

            {(opsError || opsMessage || costAlerts.length > 0) && (
              <div className="mb-6 space-y-3">
                {opsError && <Banner tone="blocked" label={opsError} />}
                {opsMessage && <Banner tone="ready" label={opsMessage} />}
                {costAlerts.map((alert) => (
                  <Banner key={`${alert.project_id}-${alert.alert_type}`} tone={alert.alert_type === "critical" ? "blocked" : "editing"} label={`${alert.alert_type} · Project ${alert.project_id} · $${alert.actual_cost_usd.toFixed(2)}`} />
                ))}
              </div>
            )}

            <section id="overview" className="grid scroll-mt-24 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <Kpi title="Total books in production" value={stats?.total ?? 0} delta="From book records" icon={<BookOpen size={24} />} />
              <Kpi title="Total revenue (MTD)" value={`$${(royalty?.total_gross_revenue ?? 0).toLocaleString()}`} delta={royalty ? "From royalty callable" : "Royalty callable not loaded"} icon={<CurrencyCircleDollar size={24} />} />
              <Kpi title="Avg. turnaround time" value={averageTurnaroundDays(projects)} delta="Start to latest movement" icon={<ChartLineUp size={24} />} />
              <Kpi title="Stuck in proofing" value={stats?.byState.AWAITING_REVIEW ?? 0} delta="Awaiting author approval" icon={<Warning size={24} />} />
              <Kpi title="Payment setup pending" value={projectsAwaitingPayment(projects)} delta="Books not marked paid" icon={<CurrencyCircleDollar size={24} />} />
              <Kpi title="System health" value={systemHealth(systemStatus)} delta={systemStatus?.last_check ?? "Status callable not loaded"} icon={<ShieldCheck size={24} />} />
            </section>

            <div id="quality" className="h-0 scroll-mt-24" aria-hidden="true" />
            <section id="projects" className="mt-4 grid scroll-mt-24 gap-4 xl:grid-cols-[1.1fr_0.8fr_0.9fr]">
              <ActiveProjects projects={projects} />
              <ReviewJobs projects={projects} />
              <Bottlenecks stats={stats} />
            </section>

            <div id="authors" className="h-0 scroll-mt-24" aria-hidden="true" />
            <div id="support" className="h-0 scroll-mt-24" aria-hidden="true" />
            <section id="payouts" className="mt-4 grid scroll-mt-24 gap-4 xl:grid-cols-[0.9fr_0.9fr_0.9fr]">
              <PayoutPanel royalty={royalty} onProcessPayouts={processPayouts} disabled={opsLoading} />
              <AuthorsPanel projects={projects} />
              <SupportPanel contacts={contacts} />
            </section>

            <div id="orders" className="h-0 scroll-mt-24" aria-hidden="true" />
            <section id="production" className="mt-4 grid scroll-mt-24 gap-4 xl:grid-cols-[1fr_1fr]">
              <ImportISBNPanel isbnBatch={isbnBatch} setIsbnBatch={setIsbnBatch} isbnNumbers={isbnNumbers} setIsbnNumbers={setIsbnNumbers} importISBNs={importISBNs} opsLoading={opsLoading} status={isbnStatus} />
              <ImportRoyaltyPanel royaltyJson={royaltyJson} setRoyaltyJson={setRoyaltyJson} importRoyaltyReport={importRoyaltyReport} opsLoading={opsLoading} />
            </section>

            <div id="channels" className="h-0 scroll-mt-24" aria-hidden="true" />
            <div id="system" className="h-0 scroll-mt-24" aria-hidden="true" />
            <section id="analytics" className="mt-4 grid scroll-mt-24 gap-4 xl:grid-cols-[1fr_1fr_0.9fr]">
              <ChannelPerformance royalty={royalty} />
              <SystemEvents systemStatus={systemStatus} />
              <AtelierSummary stats={stats} royalty={royalty} isbnStatus={isbnStatus} />
            </section>
          </main>
        </div>
      </div>
    </AppShell>
  );
}

function Kpi({ title, value, delta, icon }: { title: string; value: string | number; delta: string; icon: React.ReactNode }) {
  return (
    <PressPanel className="p-4">
      <div className="flex items-start justify-between gap-3">
        <PressEyebrow>{title}</PressEyebrow>
        <span className="text-walnut">{icon}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-semibold leading-none text-ink-1">{value}</p>
      <p className="mt-2 font-text text-sm text-ink-3">{delta}</p>
    </PressPanel>
  );
}

function ActiveProjects({ projects }: { projects: Project[] }) {
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Active books</PressEyebrow><PressEyebrow>{projects.length} total</PressEyebrow></div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left font-ui text-sm">
          <thead><tr className="border-b border-rule"><Th>Book</Th><Th>Author</Th><Th>Stage</Th><Th>Progress</Th></tr></thead>
          <tbody>
            {projects.slice(0, 6).map((project) => (
              <tr key={project.id} className="border-b border-rule-faint">
                <Td><span className="font-display text-base text-ink-1">{project.title}</span></Td>
                <Td>{project.author_name}</Td>
                <Td><StatusPill tone={toneFor(project.state)}>{operatorStateLabel(project.state)}</StatusPill></Td>
                <Td><Progress value={progressFor(project.state)} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PressPanel>
  );
}

function ReviewJobs({ projects }: { projects: Project[] }) {
  const jobs = projects.filter((project) => ["AWAITING_REVIEW", "PACKAGED", "ERROR", "EDITED"].includes(project.state)).slice(0, 5);
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Decisions requiring approval</PressEyebrow><PressEyebrow>{jobs.length} open</PressEyebrow></div>
      <div className="mt-3 space-y-3">
        {jobs.length > 0 ? jobs.map((project) => (
          <div key={project.id} className="grid grid-cols-[24px_1fr_auto] gap-3 border-b border-rule-faint pb-3 last:border-0">
            <CheckCircle size={18} className="mt-1 text-walnut" />
            <div>
              <p className="font-display text-base text-ink-1">{reviewLabel(project.state)}</p>
              <p className="font-text text-sm text-ink-3">{project.title} · {project.author_name}</p>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{stateAge(project)}</span>
          </div>
        )) : (
          <p className="font-ui text-sm leading-as-read text-ink-3">No books currently require operator approval.</p>
        )}
      </div>
    </PressPanel>
  );
}

function Bottlenecks({ stats }: { stats: PipelineStats | null }) {
  const rows = [
    ["Proofing", stats?.byState.AWAITING_REVIEW ?? 0],
    ["Cover design", stats?.byState.PACKAGING ?? 0],
    ["Interior proof", stats?.byState.EDITING ?? 0],
    ["Typesetting", stats?.byState.EDITED ?? 0],
    ["Print proof", stats?.byState.PACKAGED ?? 0],
  ];
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Production bottlenecks</PressEyebrow><PressEyebrow>Production overview</PressEyebrow></div>
      <div className="mt-3 space-y-3">
        {rows.map(([name, count]) => (
          <div key={String(name)} className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-rule-faint pb-3 last:border-0">
            <p className="font-display text-base text-ink-1">{name}</p>
            <p className="font-ui text-sm text-ink-2">{count} books</p>
          </div>
        ))}
      </div>
    </PressPanel>
  );
}

function PayoutPanel({ royalty, onProcessPayouts, disabled }: { royalty: RoyaltyDashboard | null; onProcessPayouts: () => void; disabled: boolean }) {
  const pendingPayout = royalty?.total_author_payouts ?? 0;
  const netRoyalty = royalty?.total_net_royalty ?? 0;
  const operatorShare = royalty?.total_operator_share ?? 0;
  const unitsSold = royalty?.total_units_sold ?? 0;
  return (
    <PressPanel className="p-4">
      <PressEyebrow>Payout reconciliation</PressEyebrow>
      <div className="mt-4 grid gap-4 md:grid-cols-[120px_1fr]">
        <div className="grid h-28 w-28 place-items-center rounded-full" style={{ background: "conic-gradient(var(--as-walnut) 0 36%, var(--as-verify) 36% 68%, var(--as-paper-3) 68% 100%)" }}>
          <div className="grid h-20 w-20 place-items-center rounded-full bg-paper-raised text-center">
            <div>
              <p className="font-display text-xl font-semibold text-ink-1">${pendingPayout.toLocaleString()}</p>
              <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">Pending</p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <LedgerLine label="Net royalty" value={`$${netRoyalty.toLocaleString()}`} />
          <LedgerLine label="Operator share" value={`$${operatorShare.toLocaleString()}`} />
          <LedgerLine label="Units sold" value={unitsSold.toLocaleString()} />
          <LedgerLine label="Catalog size" value={(royalty?.catalog_size ?? 0).toLocaleString()} />
          <Button variant="walnut" onClick={onProcessPayouts} disabled={disabled}>Process payouts</Button>
        </div>
      </div>
    </PressPanel>
  );
}

function AuthorsPanel({ projects }: { projects: Project[] }) {
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Author accounts</PressEyebrow><PressEyebrow>{projects.length} records</PressEyebrow></div>
      <div className="mt-3 space-y-3">
        {projects.slice(0, 5).map((project) => (
          <div key={project.id} className="grid grid-cols-[1fr_auto] border-b border-rule-faint pb-3 last:border-0">
            <div>
              <p className="font-display text-base text-ink-1">{project.author_name}</p>
              <p className="font-text text-sm text-ink-3">{project.title}</p>
            </div>
            <StatusPill tone={project.payment_status === "paid" ? "ready" : "draft"}>{paymentStatusLabel(project.payment_status)}</StatusPill>
          </div>
        ))}
      </div>
    </PressPanel>
  );
}

function SupportPanel({ contacts }: { contacts: ContactQueueItem[] }) {
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Contact queue</PressEyebrow><PressEyebrow>{contacts.length} open</PressEyebrow></div>
      <div className="mt-3 space-y-3">
        {contacts.length > 0 ? contacts.slice(0, 5).map((contact) => (
          <div key={contact.id} className="grid grid-cols-[76px_1fr_auto] gap-3 border-b border-rule-faint pb-3 font-ui text-sm last:border-0">
            <span className="font-mono text-[10px] uppercase text-ink-4">{contact.intent}</span>
            <span>
              {contact.name}
              <span className="block truncate text-ink-3">{contact.email}</span>
            </span>
            <StatusPill tone={contact.status === "new" ? "editing" : "ready"}>{contact.status}</StatusPill>
          </div>
        )) : (
          <p className="font-ui text-sm leading-as-read text-ink-3">No contact, support, or sales requests are queued.</p>
        )}
      </div>
    </PressPanel>
  );
}

function ImportISBNPanel({ isbnBatch, setIsbnBatch, isbnNumbers, setIsbnNumbers, importISBNs, opsLoading, status }: {
  isbnBatch: string;
  setIsbnBatch: (value: string) => void;
  isbnNumbers: string;
  setIsbnNumbers: (value: string) => void;
  importISBNs: (event: FormEvent) => void;
  opsLoading: boolean;
  status: ISBNInventoryStatus | null;
}) {
  return (
    <PressPanel className="p-4">
      <PressEyebrow>ISBN inventory</PressEyebrow>
      <form onSubmit={importISBNs} className="mt-4 space-y-3">
        <Input label="Purchase batch" value={isbnBatch} onChange={(e) => setIsbnBatch(e.target.value)} placeholder="BOWKER-2026-05" required />
        <label className="flex flex-col gap-[4px]">
          <span className="font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3">ISBN-13 numbers</span>
          <textarea value={isbnNumbers} onChange={(e) => setIsbnNumbers(e.target.value)} className="min-h-[108px] rounded-as-1 border border-rule bg-paper-raised px-3 py-2 font-mono text-sm text-ink-1 outline-none focus:border-walnut" required />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" variant="walnut" disabled={opsLoading}>Import ISBNs</Button>
          <PressEyebrow>Available {status?.available ?? 0} · Assigned {status?.assigned ?? 0}</PressEyebrow>
        </div>
      </form>
    </PressPanel>
  );
}

function ImportRoyaltyPanel({ royaltyJson, setRoyaltyJson, importRoyaltyReport, opsLoading }: {
  royaltyJson: string;
  setRoyaltyJson: (value: string) => void;
  importRoyaltyReport: (event: FormEvent) => void;
  opsLoading: boolean;
}) {
  return (
    <PressPanel className="p-4">
      <PressEyebrow>Royalty report import</PressEyebrow>
      <form onSubmit={importRoyaltyReport} className="mt-4 space-y-3">
        <label className="flex flex-col gap-[4px]">
          <span className="font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3">Royalty entries (JSON)</span>
          <textarea value={royaltyJson} onChange={(e) => setRoyaltyJson(e.target.value)} className="min-h-[166px] rounded-as-1 border border-rule bg-paper-raised px-3 py-2 font-mono text-sm text-ink-1 outline-none focus:border-walnut" required />
        </label>
        <Button type="submit" variant="walnut" disabled={opsLoading}>Import royalties</Button>
      </form>
    </PressPanel>
  );
}

function ChannelPerformance({ royalty }: { royalty: RoyaltyDashboard | null }) {
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Channel performance (MTD)</PressEyebrow><GlobeHemisphereWest size={22} className="text-walnut" /></div>
      {royalty ? (
        <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[520px] font-ui text-sm">
          <thead><tr className="border-b border-rule"><Th>Channel</Th><Th>Revenue</Th><Th>Units</Th><Th>Avg. payout</Th></tr></thead>
          <tbody>
              <tr className="border-b border-rule-faint">
                <Td>Imported royalty ledger</Td>
                <Td>${royalty.total_gross_revenue.toLocaleString()}</Td>
                <Td>{royalty.total_units_sold.toLocaleString()}</Td>
                <Td>${royalty.total_units_sold > 0 ? (royalty.total_net_royalty / royalty.total_units_sold).toFixed(2) : "0.00"}</Td>
              </tr>
          </tbody>
        </table>
        </div>
      ) : (
        <p className="mt-3 font-ui text-sm leading-as-read text-ink-3">Channel data appears after the royalty dashboard callable returns imported ledger totals.</p>
      )}
    </PressPanel>
  );
}

function SystemEvents({ systemStatus }: { systemStatus: SystemStatus | null }) {
  const breakers = systemStatus?.circuit_breakers ?? [];
  const events = breakers.length > 0
    ? breakers.map((breaker) => [breaker.name, breaker.tripped ? "Warning" : "Success"])
    : [];
  return (
    <PressPanel className="p-4">
      <div className="flex justify-between"><PressEyebrow>Recent system events</PressEyebrow><GearSix size={22} className="text-walnut" /></div>
      <div className="mt-3 space-y-3">
        {events.length > 0 ? events.map(([name, status], index) => (
          <div key={`${name}-${index}`} className="grid grid-cols-[18px_1fr_auto] gap-3 border-b border-rule-faint pb-3 last:border-0">
            <span className={`mt-1 h-3 w-3 rounded-full ${status === "Warning" ? "bg-walnut" : "bg-verify"}`} />
            <p className="font-display text-base text-ink-1">{name}</p>
            <StatusPill tone={status === "Warning" ? "editing" : "ready"}>{status}</StatusPill>
          </div>
        )) : (
          <p className="font-ui text-sm leading-as-read text-ink-3">No circuit-breaker events are currently reported.</p>
        )}
      </div>
    </PressPanel>
  );
}

function AtelierSummary({ stats, royalty, isbnStatus }: { stats: PipelineStats | null; royalty: RoyaltyDashboard | null; isbnStatus: ISBNInventoryStatus | null }) {
  return (
    <PressPanel className="p-4">
      <PressEyebrow>Atelier summary</PressEyebrow>
      <div className="mt-3 h-40 rounded-as-3 border border-rule bg-[radial-gradient(circle_at_24%_36%,var(--as-walnut)_0_3px,transparent_4px),radial-gradient(circle_at_62%_48%,var(--as-walnut)_0_3px,transparent_4px),radial-gradient(circle_at_78%_62%,var(--as-walnut)_0_3px,transparent_4px),linear-gradient(180deg,var(--as-paper-raised),var(--as-paper-2))]" />
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryMetric label="Active" value={stats?.active ?? 0} />
        <SummaryMetric label="Published" value={stats?.completed ?? 0} />
        <SummaryMetric label="Authors" value={royalty?.catalog_size ?? 0} />
        <SummaryMetric label="ISBNs" value={isbnStatus?.available ?? 0} />
      </div>
    </PressPanel>
  );
}

function Banner({ tone, label }: { tone: "ready" | "blocked" | "editing"; label: string }) {
  return (
    <div className={`rounded-as-2 border p-3 font-mono text-[11px] uppercase tracking-as-eyebrow ${tone === "ready" ? "border-verify bg-verify-tint text-verify" : tone === "blocked" ? "border-flag bg-flag-tint text-flag" : "border-walnut bg-walnut-tint text-walnut-deep"}`}>
      {label}
    </div>
  );
}

function LedgerLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-rule-faint pb-2 font-ui text-sm">
      <span className="text-ink-3">{label}</span>
      <span className="text-ink-1">{value}</span>
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="font-display text-xl font-semibold text-ink-1">{value}</p>
      <p className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
    </div>
  );
}

function Progress({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-20 rounded-full bg-paper-2">
      <div className="h-full rounded-full bg-walnut" style={{ width: `${value}%` }} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-3 text-ink-2">{children}</td>;
}

function systemHealth(status: SystemStatus | null): string {
  if (!status) return "Unknown";
  if (status.status === "healthy") return "Healthy";
  if (status.status === "degraded") return "Degraded";
  return "Unknown";
}

function formatDateRange(date: Date): string {
  const start = new Date(date);
  start.setDate(date.getDate() - 6);
  const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${formatter.format(start)} – ${formatter.format(date)}`;
}

function averageTurnaroundDays(projects: Project[]): string {
  const durations = projects
    .map((project) => {
      const created = project.created_at?.toMillis?.();
      const updated = project.updated_at?.toMillis?.();
      if (!created || !updated || updated < created) return null;
      return (updated - created) / 86_400_000;
    })
    .filter((value): value is number => typeof value === "number");
  if (durations.length === 0) return "0 days";
  const average = durations.reduce((total, value) => total + value, 0) / durations.length;
  return `${average.toFixed(1)} days`;
}

function projectsAwaitingPayment(projects: Project[]): number {
  return projects.filter((project) => project.payment_status !== "paid").length;
}

function stateAge(project: Project): string {
  const millis = project.updated_at?.toMillis?.();
  if (!millis) return "unknown";
  const days = Math.max(0, Math.floor((Date.now() - millis) / 86_400_000));
  if (days === 0) return "today";
  if (days === 1) return "1d";
  return `${days}d`;
}

function toneFor(state: StateMachineState): "draft" | "progress" | "editing" | "ready" | "published" | "blocked" {
  if (state === "PUBLISHED") return "published";
  if (state === "ERROR") return "blocked";
  if (state === "PACKAGED" || state === "EDITED") return "ready";
  if (state === "EDITING" || state === "AWAITING_REVIEW" || state === "REVISED") return "editing";
  if (state === "CREATED" || state === "INGESTED") return "draft";
  return "progress";
}

function progressFor(state: StateMachineState): number {
  if (state === "PUBLISHED") return 100;
  if (state === "PACKAGED") return 94;
  if (state === "EDITED") return 82;
  if (state === "AWAITING_REVIEW") return 61;
  if (state === "ERROR") return 33;
  return 48;
}

function reviewLabel(state: StateMachineState): string {
  if (state === "ERROR") return "Operator intervention";
  if (state === "PACKAGED") return "Print proof approval";
  if (state === "EDITED") return "Book details check";
  if (state === "AWAITING_REVIEW") return "Author proof";
  return "Production decision";
}

function operatorStateLabel(state: StateMachineState): string {
  const labels: Record<StateMachineState, string> = {
    CREATED: "raw source",
    INGESTED: "source in",
    TRANSCRIBING: "transcribing",
    TRANSCRIBED: "transcribed",
    ANALYZING: "finding shape",
    ANALYZED: "shape found",
    STRUCTURING: "chapter map",
    STRUCTURED: "map ready",
    DRAFTING: "manuscript build",
    DRAFTED: "proof ready",
    AWAITING_REVIEW: "proof open",
    REVISED: "revision in",
    AUTO_ADVANCED: "proof closed",
    EDITING: "editorial",
    EDITED: "edit complete",
    PACKAGING: "packet build",
    PACKAGED: "packet ready",
    PUBLISHING: "releasing",
    PUBLISHED: "published",
    ERROR: "blocked",
  };
  return labels[state];
}

function paymentStatusLabel(status: Project["payment_status"] | undefined): string {
  if (status === "paid") return "paid";
  if (status === "checkout_created") return "checkout open";
  if (status === "failed") return "failed";
  return "pending";
}

function buildPipelineStats(projects: Project[]): PipelineStats {
  const byState: Record<string, number> = {};
  for (const state of ALL_STATES) byState[state] = 0;
  let active = 0;
  let completed = 0;
  let errors = 0;
  for (const project of projects) {
    byState[project.state] = (byState[project.state] ?? 0) + 1;
    if (project.state === "PUBLISHED") completed++;
    else if (project.state === "ERROR") errors++;
    else active++;
  }
  return { byState, total: projects.length, active, completed, errors };
}

function parseRoyaltyEntries(value: string): RoyaltyReportEntry[] {
  const parsed: unknown = JSON.parse(value);
  const entries = Array.isArray(parsed)
    ? parsed
    : isRecord(parsed) && Array.isArray(parsed.entries) ? parsed.entries : null;
  if (!entries) {
    throw new Error("Royalty JSON must be an array or an object with an entries array.");
  }
  if (!entries.every(isRoyaltyReportEntry)) {
    throw new Error("Royalty JSON contains an invalid entry.");
  }
  return entries;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRoyaltyReportEntry(value: unknown): value is RoyaltyReportEntry {
  if (!isRecord(value)) return false;
  return typeof value.isbn === "string"
    && ["amazon_kdp", "ingramspark", "draft2digital", "storyforge_direct"].includes(String(value.channel))
    && typeof value.period === "string"
    && typeof value.units_sold === "number"
    && typeof value.gross_revenue === "number"
    && typeof value.platform_fees === "number"
    && typeof value.print_cost === "number";
}
